import React, { useContext } from "react";
import { Navigate, Route } from "react-router";
import ConfigurationsContext from "context/configurations/ConfigurationsContext.ts";
import { components } from "types/declarations/api";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import { UserNavigationGroup, UserNavigationGroupType, UserRoleType } from "types/declarations/enums.ts";

type FeatureFlagKey = keyof components["schemas"]["FeatureFlagsViewModel"];

type BlackListNavGroupWithRoles = Partial<Record<UserNavigationGroupType, UserRoleType[]>>;

const isUserNavigationGroupType = (value: string | null | undefined): value is UserNavigationGroupType => {
	return !!value && Object.values(UserNavigationGroup).includes(value as UserNavigationGroupType);
};

interface FeatureFlagWrapperProps {
	featureFlag?: FeatureFlagKey;
	conditionToRedirect?: boolean;
	fallbackPath?: string;
	children: React.ReactElement;
	userRolesWithAccess?: UserRoleType[];
	blackListNavGroupWithRoles?: BlackListNavGroupWithRoles;
}

const FeatureFlagWrapper: React.FC<FeatureFlagWrapperProps> = ({
	featureFlag,
	fallbackPath = "/not-found",
	conditionToRedirect,
	children,
	userRolesWithAccess = [],
	blackListNavGroupWithRoles = {}
}) => {
	const { configurationsData } = useContext(ConfigurationsContext);
	const { data: userDetails, isLoading: isUserDetailsLoading } = useGetUserDetailsQuery();

	const isConfigurationsLoading = configurationsData === undefined;

	if (isUserDetailsLoading || isConfigurationsLoading) {
		return null;
	}

	const userRole = userDetails?.role as UserRoleType | undefined;
	const userNavigationGroup = userDetails?.navigationGroup;

	const roleSpecificNavigationGroups = isUserNavigationGroupType(userNavigationGroup)
		? blackListNavGroupWithRoles[userNavigationGroup]
		: undefined;

	const userDoesNotHaveAccess =
		userRolesWithAccess?.length > 0 && userRole && !userRolesWithAccess?.includes(userRole);

	const userFailsNavigationGroupRule =
		!!roleSpecificNavigationGroups && !!userRole && roleSpecificNavigationGroups.includes(userRole);

	if (
		(featureFlag && configurationsData?.featureFlags?.[featureFlag] !== true) ||
		conditionToRedirect ||
		userDoesNotHaveAccess ||
		userFailsNavigationGroupRule
	) {
		return (
			<Navigate
				to={fallbackPath}
				replace
			/>
		);
	}

	return children;
};

interface CreateProtectedRouteProps {
	featureFlag?: FeatureFlagKey;
	conditionToRedirect?: boolean;
	path?: string;
	element?: React.ReactElement;
	fallbackPath?: string;
	index?: boolean;
	children?: React.ReactNode;
	userRolesWithAccess?: UserRoleType[];
	blackListNavGroupWithRoles?: BlackListNavGroupWithRoles;
}

export const ProtectedRoute = ({
	featureFlag,
	path,
	element,
	fallbackPath = "/not-found",
	index = false,
	children,
	conditionToRedirect,
	userRolesWithAccess = [],
	blackListNavGroupWithRoles = {}
}: CreateProtectedRouteProps) => {
	if (children) {
		const flattenChildren = (children: React.ReactNode): React.ReactElement[] => {
			const result: React.ReactElement[] = [];
			React.Children.forEach(children, (child) => {
				if (React.isValidElement(child)) {
					if (child.type === React.Fragment) {
						const childProps = child.props as { children?: React.ReactNode };
						result.push(...flattenChildren(childProps.children));
					} else {
						result.push(child);
					}
				}
			});
			return result;
		};

		const flatChildren = flattenChildren(children);

		const wrappedChildren = flatChildren.map((child) => {
			const childProps = child.props as any;

			if (childProps.element) {
				return React.cloneElement(child, {
					...childProps,
					element: (
						<FeatureFlagWrapper
							featureFlag={featureFlag}
							conditionToRedirect={conditionToRedirect}
							fallbackPath={fallbackPath}
							userRolesWithAccess={userRolesWithAccess}
							blackListNavGroupWithRoles={blackListNavGroupWithRoles}
						>
							{childProps.element}
						</FeatureFlagWrapper>
					)
				});
			}
			return child;
		});

		return <Route path={path}>{wrappedChildren}</Route>;
	}

	return (
		<Route
			path={path}
			element={
				<FeatureFlagWrapper
					featureFlag={featureFlag}
					fallbackPath={fallbackPath}
					conditionToRedirect={conditionToRedirect}
					userRolesWithAccess={userRolesWithAccess}
					blackListNavGroupWithRoles={blackListNavGroupWithRoles}
				>
					{element!}
				</FeatureFlagWrapper>
			}
			index={index}
		/>
	);
};
